/**
 * WebSocket Client Manager
 * 
 * Manages WebSocket connection, subscriptions, and message handling.
 * Implements automatic reconnection with exponential backoff and heartbeat monitoring.
 * 
 * @see Task 11.2: Socket.IO configuration with browser-specific optimizations
 */

import { io, Socket } from 'socket.io-client';
import {
  ClientConfig,
  ConnectionStatus,
  Subscription,
  MessageType,
  MessageHandler,
  ConnectionEventHandlers
} from './types';
import { FallbackManager } from './FallbackManager';
import { wsLogger } from './logger';
import {
  createSocketIOConfig,
  extractManagerOptions,
  updateConfigForNetwork,
  getConfigSummary,
  SocketIOConfig
} from './socketIOConfig';
import {
  getBrowserCapabilities,
  getNetworkInfo,
  BrowserCapabilities,
  NetworkInfo
} from './browserDetection';

/**
 * WebSocket Client Manager class
 */
export class WebSocketClientManager {
  private socket: Socket | null = null;
  private config: ClientConfig;
  private socketIOConfig: SocketIOConfig | null = null;
  private status: ConnectionStatus = ConnectionStatus.DISCONNECTED;
  private messageHandlers: Map<MessageType, Set<MessageHandler>> = new Map();
  private subscriptions: Map<string, Subscription> = new Map();
  /** 当前 socket 连接已成功 emit 的订阅 key，disconnect 时清空，防止同一连接内重复 emit */
  private activeSubscriptionKeys: Set<string> = new Set();
  private messageQueue: Array<{ event: string; data: any }> = [];
  private reconnectAttempt: number = 0;
  private lastMessageTime: number = Date.now();
  private heartbeatCheckInterval: NodeJS.Timeout | null = null;
  private connectionEventHandlers: ConnectionEventHandlers = {};
  private isFallbackMode: boolean = false;
  private fallbackManager: FallbackManager;
  private browserCapabilities: BrowserCapabilities;
  private networkInfo: NetworkInfo;

  constructor(config: ClientConfig) {
    this.config = config;
    this.fallbackManager = new FallbackManager({
      interval: 3000, // 3 seconds
      maxRetries: 10,
      enabled: true
    });

    // Initialize browser capabilities and network info
    this.browserCapabilities = getBrowserCapabilities();
    this.networkInfo = getNetworkInfo();

    wsLogger.debug('WebSocketClientManager', 'Initialized with browser capabilities', {
      browser: this.browserCapabilities.browserName,
      version: this.browserCapabilities.browserMajorVersion,
      isMobile: this.browserCapabilities.isMobile,
      isSupported: this.browserCapabilities.isSupported,
      hasWebSocket: this.browserCapabilities.hasWebSocket,
    });
  }

  /**
   * Initialize and connect to WebSocket server
   */
  async connect(): Promise<void> {
    if (this.socket?.connected) {
      wsLogger.debug('Connection', 'Already connected');
      return;
    }

    // Refresh network info before connecting
    this.networkInfo = getNetworkInfo();

    // Check if browser supports WebSocket
    if (!this.browserCapabilities.hasWebSocket) {
      wsLogger.warn('Connection', 'WebSocket not supported, activating fallback', {
        reason: this.browserCapabilities.unsupportedReason
      });
      this.activateFallback();
      return;
    }

    // Check if browser is supported
    if (!this.browserCapabilities.isSupported) {
      wsLogger.warn('Connection', 'Browser not fully supported', {
        browser: this.browserCapabilities.browserName,
        version: this.browserCapabilities.browserMajorVersion,
        reason: this.browserCapabilities.unsupportedReason
      });
      // Continue anyway, but log the warning
    }

    wsLogger.info('Connection', 'Starting connection...', {
      serverUrl: this.config.serverUrl,
      hasToken: !!this.config.authToken,
      tokenLength: this.config.authToken?.length || 0,
      browser: this.browserCapabilities.browserName,
      isMobile: this.browserCapabilities.isMobile,
      networkType: this.networkInfo.connectionType,
    });

    const oldStatus = this.status;
    this.setStatus(ConnectionStatus.CONNECTING);
    wsLogger.logConnectionStateChange(oldStatus, ConnectionStatus.CONNECTING);

    try {
      // Create optimized Socket.IO configuration
      this.socketIOConfig = createSocketIOConfig(
        this.config.serverUrl,
        this.config.authToken,
        this.browserCapabilities,
        this.networkInfo
      );

      // Log configuration summary
      wsLogger.debug('Connection', 'Socket.IO configuration', getConfigSummary(this.socketIOConfig));

      // Extract manager options from config
      const managerOptions = extractManagerOptions(this.socketIOConfig);

      // Create socket connection with optimized configuration
      this.socket = io(this.config.serverUrl, {
        ...managerOptions,
        auth: {
          token: this.config.authToken
        },
      });

      wsLogger.debug('Connection', 'Socket.IO client created', {
        socketId: this.socket.id,
        connected: this.socket.connected,
        transports: this.socketIOConfig.transports,
      });

      // Setup event handlers
      this.setupEventHandlers();

      // Start heartbeat monitoring
      this.startHeartbeatMonitoring();

      wsLogger.info('Connection', 'Connection initiated');
    } catch (error) {
      wsLogger.logConnectionError(error as Error);
      this.setStatus(ConnectionStatus.FAILED);
      throw error;
    }
  }

  /**
   * Setup socket event handlers
   */
  private setupEventHandlers(): void {
    if (!this.socket) return;

    // Connection established
    this.socket.on('connect', () => {
      const oldStatus = this.status;
      this.setStatus(ConnectionStatus.CONNECTED);
      this.reconnectAttempt = 0;
      this.lastMessageTime = Date.now();

      wsLogger.logConnected(this.socket?.id || 'unknown');
      wsLogger.logConnectionStateChange(oldStatus, ConnectionStatus.CONNECTED);

      // Restore subscriptions
      this.restoreSubscriptions();

      // Send queued messages
      this.flushMessageQueue();

      // Call connection handler
      this.connectionEventHandlers.onConnect?.();
    });

    // Disconnection
    this.socket.on('disconnect', (reason: string) => {
      const oldStatus = this.status;
      this.setStatus(ConnectionStatus.DISCONNECTED);
      // 清空当前连接的活跃订阅记录，重连后需要重新订阅
      this.activeSubscriptionKeys.clear();

      wsLogger.logDisconnected(reason);
      wsLogger.logConnectionStateChange(oldStatus, ConnectionStatus.DISCONNECTED);

      // Call disconnect handler
      this.connectionEventHandlers.onDisconnect?.(reason);
    });

    // Reconnection attempt
    this.socket.on('reconnect_attempt', (attemptNumber: number) => {
      const oldStatus = this.status;
      this.setStatus(ConnectionStatus.RECONNECTING);
      this.reconnectAttempt = attemptNumber;

      wsLogger.logReconnecting(attemptNumber);
      wsLogger.logConnectionStateChange(oldStatus, ConnectionStatus.RECONNECTING);
    });

    // Reconnection successful
    this.socket.on('reconnect', (attemptNumber: number) => {
      const oldStatus = this.status;
      this.setStatus(ConnectionStatus.CONNECTED);
      this.reconnectAttempt = 0;

      wsLogger.logReconnected(attemptNumber);
      wsLogger.logConnectionStateChange(oldStatus, ConnectionStatus.CONNECTED);

      // Deactivate fallback mode when WebSocket is restored
      if (this.isFallbackMode) {
        this.deactivateFallback();
      }

      // Call reconnect handler
      this.connectionEventHandlers.onReconnect?.(attemptNumber);
    });

    // Reconnection failed
    this.socket.on('reconnect_failed', () => {
      const oldStatus = this.status;
      this.setStatus(ConnectionStatus.FAILED);

      wsLogger.logReconnectFailed({ attempts: this.reconnectAttempt });
      wsLogger.logConnectionStateChange(oldStatus, ConnectionStatus.FAILED);

      // Activate fallback mode after 3 failed attempts
      if (this.reconnectAttempt >= 3) {
        this.activateFallback();
      }

      // Call reconnect failed handler
      this.connectionEventHandlers.onReconnectFailed?.();
    });

    // Connection error
    this.socket.on('connect_error', (error: Error) => {
      wsLogger.logConnectionError(error, { type: 'connect_error' });

      // Call error handler
      this.connectionEventHandlers.onError?.(error);
    });

    // Error event
    this.socket.on('error', (error: Error) => {
      wsLogger.logConnectionError(error, { type: 'socket_error' });

      // Call error handler
      this.connectionEventHandlers.onError?.(error);
    });

    // Message events
    this.setupMessageHandlers();
  }

  /**
   * Setup message event handlers
   */
  private setupMessageHandlers(): void {
    if (!this.socket) return;

    // Task status changed
    this.socket.on('task:status_changed', (message: any) => {
      wsLogger.logMessageReceived('task:status_changed', {
        taskId: message?.taskId,
        newStatus: message?.newStatus
      });
      this.lastMessageTime = Date.now();
      this.handleMessage(MessageType.TASK_STATUS_CHANGED, message);
    });

    // Task progress
    this.socket.on('task:progress', (message: any) => {
      wsLogger.logMessageReceived('task:progress', {
        taskId: message?.taskId,
        progress: message?.progress
      });
      this.lastMessageTime = Date.now();
      this.handleMessage(MessageType.TASK_PROGRESS, message);
    });

    // Task completed
    this.socket.on('task:completed', (message: any) => {
      wsLogger.logMessageReceived('task:completed', {
        taskId: message?.taskId
      });
      this.lastMessageTime = Date.now();
      this.handleMessage(MessageType.TASK_COMPLETED, message);
    });

    // Task failed
    this.socket.on('task:failed', (message: any) => {
      wsLogger.logMessageReceived('task:failed', {
        taskId: message?.taskId,
        error: message?.error
      });
      this.lastMessageTime = Date.now();
      this.handleMessage(MessageType.TASK_FAILED, message);
    });

    // Batch progress
    this.socket.on('batch:progress', (message: any) => {
      wsLogger.logMessageReceived('batch:progress', {
        batchId: message?.batchId,
        progress: message?.overallProgress
      });
      this.lastMessageTime = Date.now();
      this.handleMessage(MessageType.BATCH_PROGRESS, message);
    });

    // Batch completed
    this.socket.on('batch:completed', (message: any) => {
      wsLogger.logMessageReceived('batch:completed', {
        batchId: message?.batchId
      });
      this.lastMessageTime = Date.now();
      this.handleMessage(MessageType.BATCH_COMPLETED, message);
    });

    // Error
    this.socket.on('error', (message: any) => {
      wsLogger.logMessageReceived('error', message);
      this.lastMessageTime = Date.now();
      this.handleMessage(MessageType.ERROR, message);
    });

    // Heartbeat ping
    this.socket.on('heartbeat:ping', () => {
      wsLogger.logHeartbeatPing();
      this.lastMessageTime = Date.now();
      // Respond with pong
      this.socket?.emit('heartbeat:pong', { timestamp: new Date().toISOString() });
    });

    // Coin balance changed
    this.socket.on('coin:balance_changed', (message: any) => {
      wsLogger.logMessageReceived('coin:balance_changed', message);
      this.lastMessageTime = Date.now();
      this.handleMessage(MessageType.COIN_BALANCE_CHANGED, message);
    });

    // Script status changed
    this.socket.on('script:status_changed', (message: any) => {
      wsLogger.logMessageReceived('script:status_changed', {
        scriptId: message?.scriptId,
        oldStatus: message?.oldStatus,
        newStatus: message?.newStatus
      });
      this.lastMessageTime = Date.now();
      this.handleMessage(MessageType.SCRIPT_STATUS_CHANGED, message);
    });

    // Script step progress
    this.socket.on('script:step_progress', (message: any) => {
      wsLogger.logMessageReceived('script:step_progress', {
        scriptId: message?.scriptId,
        stepNumber: message?.stepNumber,
        stepName: message?.stepName,
        progress: message?.progress
      });
      this.lastMessageTime = Date.now();
      this.handleMessage(MessageType.SCRIPT_STEP_PROGRESS, message);
    });

    // Script shots generated
    this.socket.on('script:shots_generated', (message: any) => {
      wsLogger.logMessageReceived('script:shots_generated', {
        scriptId: message?.scriptId,
        startIndex: message?.startIndex,
        newShotsCount: message?.newShots?.length,
        totalShotCount: message?.totalShotCount,
        isComplete: message?.isComplete
      });
      this.lastMessageTime = Date.now();
      this.handleMessage(MessageType.SCRIPT_SHOTS_GENERATED, message);
    });

    // Preparation progress
    this.socket.on('preparation:progress', (message: any) => {
      wsLogger.logMessageReceived('preparation:progress', {
        jobId: message?.jobId,
        status: message?.status,
        currentStep: message?.currentStep
      });
      this.lastMessageTime = Date.now();
      this.handleMessage(MessageType.PREPARATION_PROGRESS, message);
    });

    // Preparation story info (streaming)
    this.socket.on('preparation:story_info', (message: any) => {
      wsLogger.logMessageReceived('preparation:story_info', {
        jobId: message?.jobId,
        field: message?.field,
        isComplete: message?.isComplete
      });
      this.lastMessageTime = Date.now();
      this.handleMessage(MessageType.PREPARATION_STORY_INFO, message);
    });

    // Preparation completed
    this.socket.on('preparation:completed', (message: any) => {
      wsLogger.logMessageReceived('preparation:completed', {
        jobId: message?.jobId,
        scriptId: message?.scriptId,
        episodeId: message?.episodeId
      });
      this.lastMessageTime = Date.now();
      this.handleMessage(MessageType.PREPARATION_COMPLETED, message);
    });

    // Preparation failed
    this.socket.on('preparation:failed', (message: any) => {
      wsLogger.logMessageReceived('preparation:failed', {
        jobId: message?.jobId,
        error: message?.error
      });
      this.lastMessageTime = Date.now();
      this.handleMessage(MessageType.PREPARATION_FAILED, message);
    });

    // Script Step 1 storyline (streaming)
    this.socket.on('script:step1_storyline', (message: any) => {
      wsLogger.logMessageReceived('script:step1_storyline', {
        scriptId: message?.scriptId,
        field: message?.field,
        isComplete: message?.isComplete
      });
      this.lastMessageTime = Date.now();
      this.handleMessage(MessageType.SCRIPT_STEP1_STORYLINE, message);
    });

    // Episode title updated
    this.socket.on('episode:title_updated', (message: any) => {
      wsLogger.logMessageReceived('episode:title_updated', {
        episodeId: message?.episodeId,
        title: message?.title
      });
      this.lastMessageTime = Date.now();
      this.handleMessage(MessageType.EPISODE_TITLE_UPDATED, message);
    });

    // Project setting step started
    this.socket.on('project_setting:step_started', (message: any) => {
      wsLogger.logMessageReceived('project_setting:step_started', {
        projectId: message?.projectId,
        stepType: message?.stepType,
        stepIndex: message?.stepIndex
      });
      this.lastMessageTime = Date.now();
      this.handleMessage(MessageType.PROJECT_SETTING_STEP_STARTED, message);
    });

    // Project setting step progress (streaming)
    this.socket.on('project_setting:step_progress', (message: any) => {
      wsLogger.logMessageReceived('project_setting:step_progress', {
        projectId: message?.projectId,
        stepType: message?.stepType,
        isComplete: message?.isComplete
      });
      this.lastMessageTime = Date.now();
      this.handleMessage(MessageType.PROJECT_SETTING_STEP_PROGRESS, message);
    });

    // Project setting step completed
    this.socket.on('project_setting:step_completed', (message: any) => {
      wsLogger.logMessageReceived('project_setting:step_completed', {
        projectId: message?.projectId,
        stepType: message?.stepType
      });
      this.lastMessageTime = Date.now();
      this.handleMessage(MessageType.PROJECT_SETTING_STEP_COMPLETED, message);
    });

    // Project setting step failed
    this.socket.on('project_setting:step_failed', (message: any) => {
      wsLogger.logMessageReceived('project_setting:step_failed', {
        projectId: message?.projectId,
        stepType: message?.stepType,
        error: message?.error
      });
      this.lastMessageTime = Date.now();
      this.handleMessage(MessageType.PROJECT_SETTING_STEP_FAILED, message);
    });

    // Project setting project meta updated
    this.socket.on('project_setting:project_meta_updated', (message: any) => {
      wsLogger.logMessageReceived('project_setting:project_meta_updated', {
        projectId: message?.projectId,
        title: message?.title
      });
      this.lastMessageTime = Date.now();
      this.handleMessage(MessageType.PROJECT_SETTING_PROJECT_META_UPDATED, message);
    });
  }

  /**
   * Handle incoming message
   */
  private handleMessage(messageType: MessageType, message: any): void {
    const handlers = this.messageHandlers.get(messageType);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(message);
        } catch (error) {
          wsLogger.logMessageHandlerError(messageType, error as Error);
        }
      });
    }
  }

  /**
   * Register message handler
   */
  onMessage(messageType: MessageType, handler: MessageHandler): () => void {
    if (!this.messageHandlers.has(messageType)) {
      this.messageHandlers.set(messageType, new Set());
    }

    this.messageHandlers.get(messageType)!.add(handler);

    // Return unsubscribe function
    return () => {
      this.messageHandlers.get(messageType)?.delete(handler);
    };
  }

  /**
   * Register connection event handlers
   */
  setConnectionEventHandlers(handlers: ConnectionEventHandlers): void {
    this.connectionEventHandlers = handlers;
  }

  /**
   * Subscribe to task updates
   */
  async subscribe(subscription: Subscription): Promise<void> {
    const key = this.getSubscriptionKey(subscription);

    // Store subscription（用于断线重连后恢复）
    this.subscriptions.set(key, subscription);

    // Send subscription request if connected
    if (this.socket?.connected) {
      // 同一个 socket 连接内，已成功订阅的 key 不重复 emit
      if (this.activeSubscriptionKeys.has(key)) {
        wsLogger.debug('Subscription', `Skipped duplicate subscribe: ${key}`);
        return;
      }
      return new Promise((resolve, reject) => {
        this.socket!.emit('subscribe', subscription, (response: any) => {
          if (response.success) {
            this.activeSubscriptionKeys.add(key);
            wsLogger.logSubscribed(subscription);
            resolve();
          } else {
            wsLogger.logSubscriptionError(subscription, response.error);
            reject(new Error(response.error));
          }
        });
      });
    } else {
      // Queue subscription for when connection is established
      wsLogger.debug('Subscription', 'Queued subscription (not connected)', subscription);
    }
  }

  /**
   * Unsubscribe from task updates
   */
  async unsubscribe(taskId: string): Promise<void> {
    // Remove from local subscriptions
    for (const [key, sub] of this.subscriptions.entries()) {
      if (sub.taskId === taskId) {
        this.subscriptions.delete(key);
        this.activeSubscriptionKeys.delete(key);
        break;
      }
    }

    // Send unsubscribe request if connected
    if (this.socket?.connected) {
      return new Promise((resolve, reject) => {
        this.socket!.emit('unsubscribe', taskId, (response: any) => {
          if (response.success) {
            wsLogger.logUnsubscribed(taskId);
            resolve();
          } else {
            wsLogger.error('Subscription', `Unsubscribe failed: ${response.error}`, { taskId });
            reject(new Error(response.error));
          }
        });
      });
    }
  }

  /**
   * Unsubscribe from episode updates
   */
  async unsubscribeEpisode(episodeId: string): Promise<void> {
    // Remove from local subscriptions
    const key = `episode:${episodeId}`;
    this.subscriptions.delete(key);
    this.activeSubscriptionKeys.delete(key);

    // Send unsubscribe request if connected
    if (this.socket?.connected) {
      return new Promise((resolve, reject) => {
        this.socket!.emit('unsubscribe:episode', episodeId, (response: any) => {
          if (response.success) {
            wsLogger.logUnsubscribed(`episode:${episodeId}`);
            resolve();
          } else {
            wsLogger.error('Subscription', `Unsubscribe episode failed: ${response.error}`, { episodeId });
            reject(new Error(response.error));
          }
        });
      });
    }
  }

  /**
   * Restore subscriptions after reconnection
   */
  private async restoreSubscriptions(): Promise<void> {
    const count = this.subscriptions.size;
    wsLogger.debug('Subscription', `Restoring ${count} subscriptions...`);

    for (const subscription of this.subscriptions.values()) {
      try {
        await this.subscribe(subscription);
      } catch (error) {
        wsLogger.error('Subscription', 'Failed to restore subscription', { subscription, error });
      }
    }

    wsLogger.logSubscriptionsRestored(count);
  }

  /**
   * Get subscription key
   */
  private getSubscriptionKey(subscription: Subscription): string {
    if (subscription.type === 'task' && subscription.taskId) {
      return `task:${subscription.taskId}`;
    } else if (subscription.type === 'taskType' && subscription.taskType) {
      return `taskType:${subscription.taskType}`;
    } else if (subscription.type === 'batch' && subscription.batchId) {
      return `batch:${subscription.batchId}`;
    } else if (subscription.type === 'episode' && subscription.episodeId) {
      return `episode:${subscription.episodeId}`;
    } else if (subscription.type === 'project' && subscription.projectId) {
      return `project:${subscription.projectId}`;
    }
    return 'unknown';
  }

  /**
   * Unsubscribe from project updates
   */
  async unsubscribeProject(projectId: string): Promise<void> {
    // Remove from local subscriptions
    const key = `project:${projectId}`;
    this.subscriptions.delete(key);
    this.activeSubscriptionKeys.delete(key);

    // Send unsubscribe request if connected
    if (this.socket?.connected) {
      return new Promise((resolve, reject) => {
        this.socket!.emit('unsubscribe:project', projectId, (response: any) => {
          if (response.success) {
            wsLogger.logUnsubscribed(`project:${projectId}`);
            resolve();
          } else {
            wsLogger.error('Subscription', `Unsubscribe project failed: ${response.error}`, { projectId });
            reject(new Error(response.error));
          }
        });
      });
    }
  }

  /**
   * Queue message for sending when connected
   */
  private queueMessage(event: string, data: any): void {
    this.messageQueue.push({ event, data });
  }

  /**
   * Flush queued messages
   */
  private flushMessageQueue(): void {
    if (!this.socket?.connected || this.messageQueue.length === 0) {
      return;
    }

    const count = this.messageQueue.length;
    wsLogger.logMessageQueueFlushed(count);

    while (this.messageQueue.length > 0) {
      const { event, data } = this.messageQueue.shift()!;
      this.socket.emit(event, data);
      wsLogger.logMessageSent(event, data);
    }
  }

  /**
   * Start heartbeat monitoring
   */
  private startHeartbeatMonitoring(): void {
    // Clear existing interval
    if (this.heartbeatCheckInterval) {
      clearInterval(this.heartbeatCheckInterval);
    }

    // Check heartbeat every 10 seconds
    this.heartbeatCheckInterval = setInterval(() => {
      const timeSinceLastMessage = Date.now() - this.lastMessageTime;

      // If no message received for heartbeatTimeout, reconnect
      if (timeSinceLastMessage > this.config.heartbeatTimeout) {
        wsLogger.logHeartbeatTimeout(timeSinceLastMessage);

        this.socket?.disconnect();
        this.socket?.connect();
      }
    }, 10000);
  }

  /**
   * Stop heartbeat monitoring
   */
  private stopHeartbeatMonitoring(): void {
    if (this.heartbeatCheckInterval) {
      clearInterval(this.heartbeatCheckInterval);
      this.heartbeatCheckInterval = null;
    }
  }

  /**
   * Activate fallback mode (polling)
   */
  private activateFallback(): void {
    wsLogger.logFallbackActivated();
    this.isFallbackMode = true;
    this.fallbackManager.activatePolling();
  }

  /**
   * Deactivate fallback mode
   */
  deactivateFallback(): void {
    wsLogger.logFallbackDeactivated();
    this.isFallbackMode = false;
    this.fallbackManager.deactivatePolling();
  }

  /**
   * Get fallback manager
   */
  getFallbackManager(): FallbackManager {
    return this.fallbackManager;
  }

  /**
   * Set connection status
   */
  private setStatus(status: ConnectionStatus): void {
    this.status = status;
  }

  /**
   * Get connection status
   */
  getStatus(): ConnectionStatus {
    return this.status;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected === true;
  }

  /**
   * Check if in fallback mode
   */
  isFallback(): boolean {
    return this.isFallbackMode;
  }

  /**
   * Get reconnect attempt number
   */
  getReconnectAttempt(): number {
    return this.reconnectAttempt;
  }

  /**
   * Get browser capabilities
   */
  getBrowserCapabilities(): BrowserCapabilities {
    return this.browserCapabilities;
  }

  /**
   * Get current network info
   */
  getNetworkInfo(): NetworkInfo {
    return this.networkInfo;
  }

  /**
   * Get current Socket.IO configuration
   */
  getSocketIOConfig(): SocketIOConfig | null {
    return this.socketIOConfig;
  }

  /**
   * Update configuration when network changes
   * Call this when network conditions change (e.g., WiFi to cellular)
   */
  updateNetworkConfig(): void {
    // Refresh network info
    const newNetworkInfo = getNetworkInfo();

    // Check if network changed significantly
    const networkChanged =
      newNetworkInfo.connectionType !== this.networkInfo.connectionType ||
      newNetworkInfo.isOnline !== this.networkInfo.isOnline;

    if (!networkChanged) {
      return;
    }

    wsLogger.info('Connection', 'Network changed, updating configuration', {
      oldNetwork: this.networkInfo.connectionType,
      newNetwork: newNetworkInfo.connectionType,
      wasOnline: this.networkInfo.isOnline,
      isOnline: newNetworkInfo.isOnline,
    });

    this.networkInfo = newNetworkInfo;

    // If we went offline, don't try to reconnect
    if (!newNetworkInfo.isOnline) {
      wsLogger.info('Connection', 'Network offline, pausing reconnection');
      return;
    }

    // If we came back online, reconnect
    if (!this.networkInfo.isOnline && newNetworkInfo.isOnline) {
      wsLogger.info('Connection', 'Network restored, reconnecting');
      this.reconnect();
      return;
    }

    // Update Socket.IO config if connected
    if (this.socketIOConfig && this.socket) {
      const updatedConfig = updateConfigForNetwork(this.socketIOConfig, newNetworkInfo);

      wsLogger.debug('Connection', 'Updated Socket.IO config for network change', updatedConfig);

      // Note: Socket.IO doesn't support dynamic config updates
      // We would need to reconnect to apply new settings
      // For now, just log the recommended changes
    }
  }

  /**
   * Force reconnection with updated configuration
   */
  reconnect(): void {
    wsLogger.info('Connection', 'Forcing reconnection...');

    if (this.socket) {
      this.socket.disconnect();
      this.socket.connect();
    } else {
      this.connect();
    }
  }

  /**
   * Pause heartbeat monitoring (e.g., when tab is hidden)
   */
  pauseHeartbeat(): void {
    wsLogger.debug('Heartbeat', 'Pausing heartbeat monitoring');
    this.stopHeartbeatMonitoring();
  }

  /**
   * Resume heartbeat monitoring (e.g., when tab becomes visible)
   */
  resumeHeartbeat(): void {
    wsLogger.debug('Heartbeat', 'Resuming heartbeat monitoring');
    this.startHeartbeatMonitoring();
  }

  /**
   * Check if connection is still alive
   * Useful after tab becomes visible or device wakes up
   */
  checkConnection(): void {
    if (!this.socket) {
      wsLogger.debug('Connection', 'No socket, cannot check connection');
      return;
    }

    if (!this.socket.connected) {
      wsLogger.info('Connection', 'Socket disconnected, reconnecting');
      this.socket.connect();
      return;
    }

    // Check time since last message
    const timeSinceLastMessage = Date.now() - this.lastMessageTime;
    const threshold = this.config.heartbeatTimeout / 2; // Half of heartbeat timeout

    if (timeSinceLastMessage > threshold) {
      wsLogger.info('Connection', 'Connection may be stale, reconnecting', {
        timeSinceLastMessage,
        threshold,
      });
      this.socket.disconnect();
      this.socket.connect();
    } else {
      wsLogger.debug('Connection', 'Connection appears healthy', {
        timeSinceLastMessage,
      });
    }
  }

  /**
   * Disconnect from server
   */
  disconnect(): void {
    wsLogger.info('Connection', 'Disconnecting...');

    // Stop heartbeat monitoring
    this.stopHeartbeatMonitoring();

    // Cleanup fallback manager
    this.fallbackManager.cleanup();

    // Disconnect socket
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    // Clear subscriptions
    this.subscriptions.clear();

    // Clear message handlers
    this.messageHandlers.clear();

    // Clear message queue
    this.messageQueue = [];

    const oldStatus = this.status;
    this.setStatus(ConnectionStatus.DISCONNECTED);
    wsLogger.logConnectionStateChange(oldStatus, ConnectionStatus.DISCONNECTED);
  }

  /**
   * Update auth token
   */
  updateAuthToken(token: string): void {
    this.config.authToken = token;

    // Reconnect with new token
    if (this.socket?.connected) {
      this.disconnect();
      this.connect();
    }
  }
}

/**
 * Get WebSocket server URL
 * Priority: env variable > current origin (production) > localhost (dev)
 */
function getServerUrl(): string {
  // 1. 优先使用环境变量（如果设置了）
  if (import.meta.env.VITE_WS_URL) {
    return import.meta.env.VITE_WS_URL;
  }

  // 2. 生产环境：使用当前页面的 origin
  if (typeof window !== 'undefined' && window.location.origin !== 'http://localhost:3001') {
    return window.location.origin;
  }

  // 3. 开发环境默认值
  return 'http://localhost:3000';
}

/**
 * Create default client configuration
 */
export function createDefaultConfig(authToken: string): ClientConfig {
  const serverUrl = getServerUrl();

  wsLogger.debug('Config', 'Creating config', {
    serverUrl,
    hasToken: !!authToken,
    tokenLength: authToken?.length || 0
  });

  return {
    serverUrl,
    authToken,
    reconnectAttempts: 5,
    reconnectDelay: 1000,
    reconnectDelayMax: 30000,
    heartbeatTimeout: 60000
  };
}
